// Credential redemption for the two admin screens that arrive holding a
// one-time credential: /accept-invite and /reset-password.
//
// Extracted from those components for one reason: they each had a copy of this
// logic, and both copies accepted a redeem that produced NO SESSION. They
// checked the returned `error` but then did `data.session?.access_token ?? null`
// and carried on, so the screen advanced to the password form with nothing
// signed in. The failure surfaced one step later, when updateUser() reported
// "Auth session missing" — blaming the password the user had just typed for a
// problem that was really in the link.
//
// The fix is in the RETURN TYPE, not in a check that a future edit can drop:
// `accessToken` is a required `string`, so "redeemed but no session" cannot be
// represented. Every branch must either produce a token or throw.
//
// Declared against a structural `AuthLike` rather than importing SupabaseClient
// so this is testable with a plain object — apps/admin has no jsdom, so a
// component test could not reach any of it.

export type RedeemKind = 'invite' | 'recovery';

export type Credentials =
  | { via: 'session'; accessToken: string; refreshToken: string }
  | { via: 'code'; code: string }
  | { via: 'tokenHash'; tokenHash: string };

type SessionLike = { access_token?: string | null } | null;
type UserLike = { email?: string | null } | null;
type AuthOutcome = { data?: { session?: SessionLike; user?: UserLike } | null; error?: unknown };

export type AuthLike = {
  setSession(args: { access_token: string; refresh_token: string }): Promise<AuthOutcome>;
  exchangeCodeForSession(code: string): Promise<AuthOutcome>;
  verifyOtp(args: { token_hash: string; type: RedeemKind }): Promise<AuthOutcome>;
};

/** Thrown when the URL carries nothing redeemable, or the redeem left no session. */
export class RedeemError extends Error {}

const NO_CREDENTIALS = 'This link is missing the credentials needed to continue.';
const NO_SESSION =
  'This link is no longer valid — it may have expired or already been used. Request a new one.';

/**
 * What the URL is carrying, and which flow it names.
 *
 * `kind` is read from `type` in either the fragment or the query, and is
 * INDEPENDENT of `creds`: a recovery link can arrive by any of the three
 * transports, and verifyOtp needs to be told which one it is.
 */
export function readCredentials(
  search: string,
  hash: string,
): { creds: Credentials | null; kind: RedeemKind } {
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  const query = new URLSearchParams(search.replace(/^\?/, ''));
  const kind: RedeemKind =
    (fragment.get('type') ?? query.get('type') ?? '').toLowerCase() === 'recovery'
      ? 'recovery'
      : 'invite';

  const accessToken = fragment.get('access_token');
  const refreshToken = fragment.get('refresh_token');
  // Fragment first: GoTrue's implicit flow delivers a usable session outright,
  // so it needs no exchange and cannot fail on a missing PKCE verifier.
  if (accessToken && refreshToken) {
    return { creds: { via: 'session', accessToken, refreshToken }, kind };
  }
  const code = query.get('code');
  if (code) return { creds: { via: 'code', code }, kind };
  const tokenHash = query.get('token_hash');
  if (tokenHash) return { creds: { via: 'tokenHash', tokenHash }, kind };
  return { creds: null, kind };
}

/**
 * Redeem `creds` into a live session, or throw.
 *
 * Resolves only with a real access token. A branch that returns no error but
 * also no session is treated as a failure, because that is exactly the state
 * that used to reach the password form.
 */
export async function redeemCredentials(
  auth: AuthLike,
  creds: Credentials | null,
  kind: RedeemKind,
): Promise<{ email: string | null; accessToken: string }> {
  if (!creds) throw new RedeemError(NO_CREDENTIALS);

  const outcome =
    creds.via === 'session'
      ? await auth.setSession({
          access_token: creds.accessToken,
          refresh_token: creds.refreshToken,
        })
      : creds.via === 'code'
        ? await auth.exchangeCodeForSession(creds.code)
        : await auth.verifyOtp({ token_hash: creds.tokenHash, type: kind });

  if (outcome.error) throw outcome.error;

  // setSession is handed the token directly, so prefer the value we already
  // hold: supabase-js does not always echo `session` back on that call, and
  // reading only from the response would make the safest transport the one
  // most likely to look session-less.
  const accessToken =
    creds.via === 'session'
      ? creds.accessToken
      : (outcome.data?.session?.access_token ?? null);
  if (!accessToken) throw new RedeemError(NO_SESSION);

  return { email: outcome.data?.user?.email ?? null, accessToken };
}
